import { type Country } from './country';
import { sheetCodeToCountry } from './fixture';
import groupRostersJson from './wc26GroupRosters.json';

/**
 * Group membership and order from `audit/wc26_fixtures-with-codes.xlsx`:
 * Teams sheet (Seed / Middle / Outsider) + Group Stage sheet (group letter).
 * Order per group: seed (1st), middles (2nd–3rd, earliest matchday), outsider (4th).
 *
 * Regenerate: `python3 scripts/generate-wc26-groups.py`
 */
export type WorldCupGroupId =
  | 'Group A'
  | 'Group B'
  | 'Group C'
  | 'Group D'
  | 'Group E'
  | 'Group F'
  | 'Group G'
  | 'Group H'
  | 'Group I'
  | 'Group J'
  | 'Group K'
  | 'Group L';

export const WORLD_CUP_GROUP_IDS: WorldCupGroupId[] = [
  'Group A',
  'Group B',
  'Group C',
  'Group D',
  'Group E',
  'Group F',
  'Group G',
  'Group H',
  'Group I',
  'Group J',
  'Group K',
  'Group L',
];

const GROUP_LETTER_ORDER = 'ABCDEFGHIJKL';

const groupRostersByCode = groupRostersJson as Record<WorldCupGroupId, string[]>;

function codesToCountries(codes: string[]): Country[] {
  return codes.map((code) => {
    const country = sheetCodeToCountry[code];
    if (!country) {
      throw new Error(`[wc26Groups] Unknown sheet code: ${code}`);
    }
    return country;
  });
}

export function getWorldCupGroups(): Record<WorldCupGroupId, Country[]> {
  const result = {} as Record<WorldCupGroupId, Country[]>;
  for (const id of WORLD_CUP_GROUP_IDS) {
    const codes = groupRostersByCode[id];
    if (!codes || codes.length !== 4) {
      throw new Error(`[wc26Groups] ${id} must have 4 teams in wc26GroupRosters.json`);
    }
    result[id] = codesToCountries(codes);
  }
  return result;
}

export const worldCupGroups = getWorldCupGroups();

export function groupTitleShort(groupId: WorldCupGroupId): string {
  const letter = groupId.replace('Group ', '');
  return `Group ${letter}`;
}

export { apiGroupToWorldCupGroupId } from '../helpers/fixtures/apiGroupToWorldCupGroupId';

export function sortGroupIds(ids: WorldCupGroupId[]): WorldCupGroupId[] {
  return [...ids].sort(
    (a, b) => GROUP_LETTER_ORDER.indexOf(a.slice(-1)) - GROUP_LETTER_ORDER.indexOf(b.slice(-1)),
  );
}

export function getWorldCupGroupForCountry(country: Country): WorldCupGroupId | undefined {
  for (const id of WORLD_CUP_GROUP_IDS) {
    if (worldCupGroups[id].includes(country)) {
      return id;
    }
  }
  return undefined;
}

/** 1-based slot in group roster (seed = 1, outsider = 4 when standard layout). */
export function getWorldCupGroupSlotIndex(country: Country): number | undefined {
  const groupId = getWorldCupGroupForCountry(country);
  if (!groupId) return undefined;
  const idx = worldCupGroups[groupId].indexOf(country);
  return idx >= 0 ? idx + 1 : undefined;
}
